import axios from 'axios';
import Cookies from 'js-cookie';
import { rootAPI } from './api';

export const SESSION_INTERVAL = 30000;
export function startSessionMonitor({
  userId,
  onEnd,
  client = axios,
  getToken = () => Cookies.get('token') || localStorage.getItem('token') || '',
}) {
  let stopped = false;
  let ended = false;
  let busy = false;
  const token = getToken();
  const current = () => !stopped && !ended && getToken() === token;
  const finish = (code) => {
    if (!current()) return;
    ended = true;
    onEnd(code);
  };
  const terminalCode = (failure) => {
    const { status, data } = failure.response || {};
    if (status === 403 && data?.code === 'ACCOUNT_DISABLED') return data.code;
    if (status === 401 && data?.code === 'SESSION_INVALID') return data.code;
    return null;
  };
  const check = async () => {
    if (!current() || busy) return;
    if (!token) {
      finish('SESSION_INVALID');
      return;
    }
    busy = true;
    try {
      const { data } = await client.get(`${rootAPI}/api/basic/v2.0.0/auth/session`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      if (data?.success && data.data?.userId === userId && data.data?.status !== 'Approved') finish('ACCOUNT_DISABLED');
    } catch (failure) {
      const code = terminalCode(failure);
      if (code) finish(code);
    } finally {
      busy = false;
    }
  };
  const interceptor = client.interceptors.response.use(
    (response) => response,
    (failure) => {
      const code = terminalCode(failure);
      if (code) {
        try {
          const target = new URL(failure.config?.url, failure.config?.baseURL || window.location.origin);
          const api = new URL(rootAPI, window.location.origin);
          const authorization = failure.config?.headers?.Authorization || failure.config?.headers?.authorization;
          if (
            target.origin === api.origin &&
            target.pathname.startsWith(`${api.pathname.replace(/\/$/, '')}/api/`) &&
            authorization === `Bearer ${token}`
          )
            finish(code);
        } catch (_) {
          /* Ignore requests outside the authenticated API. */
        }
      }
      return Promise.reject(failure);
    },
  );
  const onVisible = () => {
    if (document.visibilityState === 'visible') check();
  };
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(check, SESSION_INTERVAL);
  check();
  return () => {
    stopped = true;
    clearInterval(timer);
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', onVisible);
    client.interceptors.response.eject(interceptor);
  };
}
